import { landingApiRequest } from '@/lib/landing-api'

export const PAID_WIDGET_FLOW_STEPS = [
  'numbers_reveal',
  'post_payment',
  'questionnaire',
  'roulette',
  'simple_success',
] as const

export type PaidWidgetFlowStep = (typeof PAID_WIDGET_FLOW_STEPS)[number]

export type ThankYouResumePayload = {
  checkout_token?: string
  account_access?: boolean
  campaign_id: number
  slug: string
  payment_id: number
  status: string
  amount: number
  donation_only: boolean
  lucky_numbers: string[]
  meta_purchase_event_id: string
}

export type ThankYouHandoffPayload = {
  thank_you_url: string
  event_id: string
}

const PAID_STEP_SET = new Set<string>(PAID_WIDGET_FLOW_STEPS)
const thankYouResumeConsumeCache = new Map<string, Promise<ThankYouResumePayload>>()
const thankYouHandoffCache = new Map<string, Promise<ThankYouHandoffPayload | null>>()

export function saveThankYouResumeSnapshot(snapshot: ThankYouResumePayload): void {
  try {
    window.localStorage.setItem(
      `legalprime:thankYouResume:${snapshot.campaign_id}`,
      JSON.stringify({
        snapshot,
        savedAt: Date.now(),
      })
    )
  } catch {
    // O widget ainda pode consultar a compra com o cookie de retorno.
  }
}

export function readThankYouResumeSnapshot(
  campaignId: number
): ThankYouResumePayload | null {
  try {
    const raw = window.localStorage.getItem(`legalprime:thankYouResume:${campaignId}`)
    if (!raw) return null
    const saved = JSON.parse(raw) as { savedAt: number; snapshot: ThankYouResumePayload }
    const snapshot = saved.snapshot
    if (
      !Number.isFinite(saved.savedAt) ||
      Date.now() - saved.savedAt > 3 * 24 * 60 * 60 * 1000 ||
      snapshot?.campaign_id !== campaignId ||
      !Number.isInteger(snapshot.payment_id) ||
      snapshot.payment_id <= 0 ||
      snapshot.status !== 'paid' ||
      !Array.isArray(snapshot.lucky_numbers)
    ) {
      return null
    }
    return snapshot
  } catch {
    return null
  }
}

export function widgetFlowStorageKey(campaignId: number, comboId?: number): string {
  const base = `legalprime:widgetFlow:${campaignId}`
  return comboId != null && comboId > 0 ? `${base}:combo:${comboId}` : base
}

function isPaidWidgetFlowRecord(campaignId: number, raw: string | null): boolean {
  if (!raw) {
    return false
  }

  try {
    const parsed = JSON.parse(raw) as { step?: unknown; campaignId?: unknown }
    if (String(parsed.campaignId ?? '') !== String(campaignId)) {
      return false
    }
    return typeof parsed.step === 'string' && PAID_STEP_SET.has(parsed.step)
  } catch {
    return false
  }
}

export function hasResumablePaidWidgetFlow(campaignId: number): boolean {
  if (typeof window === 'undefined' || campaignId <= 0) {
    return false
  }

  const prefix = `legalprime:widgetFlow:${campaignId}`
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (!key || (key !== prefix && !key.startsWith(`${prefix}:combo:`))) {
        continue
      }
      if (isPaidWidgetFlowRecord(campaignId, window.localStorage.getItem(key))) {
        return true
      }
    }
  } catch {
    return false
  }

  return false
}

export function readThankYouResumeToken(search: string): string | null {
  const token = new URLSearchParams(search).get('r')?.trim().toLowerCase() ?? ''
  return /^[a-f0-9]{64}$/.test(token) ? token : null
}

export function stripThankYouResumeTokenFromUrl(): void {
  if (typeof window === 'undefined') {
    return
  }

  const url = new URL(window.location.href)
  if (!url.searchParams.has('r')) {
    return
  }

  url.searchParams.delete('r')
  const query = url.searchParams.toString()
  const next = `${url.pathname}${query ? `?${query}` : ''}${url.hash}`
  window.history.replaceState(window.history.state, '', next)
}

export async function requestThankYouHandoff(
  campaignId: number,
  paymentId: number,
  proof?: {
    checkoutToken?: string | null
    phone?: string | null
    email?: string | null
    documentCpf?: string | null
  }
): Promise<ThankYouHandoffPayload> {
  const body: Record<string, string> = {}
  const checkoutToken = proof?.checkoutToken?.trim() ?? ''
  const phone = (proof?.phone ?? '').replace(/\D/g, '')
  const email = proof?.email?.trim().toLowerCase() ?? ''
  const documentCpf = (proof?.documentCpf ?? '').replace(/\D/g, '')

  if (checkoutToken) {
    body.checkout_token = checkoutToken
  }
  if (phone) {
    body.phone = phone
  }
  if (email) {
    body.email = email
  }
  if (documentCpf) {
    body.document_cpf = documentCpf
  }

  return landingApiRequest<ThankYouHandoffPayload>(
    `/api/campaigns/${campaignId}/payments/pix/${paymentId}/thank-you-handoff`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  )
}

export function isSafeThankYouRedirectUrl(url: string): boolean {
  try {
    const parsed = new URL(url.trim())
    if (parsed.username || parsed.password) {
      return false
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false
    }
    if (!/^\/campanha\/[a-z0-9]+(?:-[a-z0-9]+)*\/obrigado\/?$/i.test(parsed.pathname)) {
      return false
    }

    const queryKeys: string[] = []
    parsed.searchParams.forEach((_value, key) => {
      queryKeys.push(key)
    })
    if (queryKeys.length !== 1 || queryKeys[0] !== 'r') {
      return false
    }

    const token = (parsed.searchParams.get('r') ?? '').trim().toLowerCase()
    return /^[a-f0-9]{64}$/.test(token)
  } catch {
    return false
  }
}

const THANK_YOU_HANDOFF_RETRY_DELAYS_MS = [0, 220, 500] as const

function waitMs(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs)
  })
}

export async function requestThankYouHandoffWithRetry(
  campaignId: number,
  paymentId: number,
  proof?: {
    checkoutToken?: string | null
    phone?: string | null
    email?: string | null
    documentCpf?: string | null
  }
): Promise<ThankYouHandoffPayload | null> {
  const cacheKey = `${campaignId}:${paymentId}`
  const cached = thankYouHandoffCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const request = (async (): Promise<ThankYouHandoffPayload | null> => {
    for (let index = 0; index < THANK_YOU_HANDOFF_RETRY_DELAYS_MS.length; index += 1) {
      const delayMs = THANK_YOU_HANDOFF_RETRY_DELAYS_MS[index]
      if (delayMs > 0) {
        await waitMs(delayMs)
      }

      try {
        const payload = await requestThankYouHandoff(campaignId, paymentId, proof)
        const thankYouUrl = payload.thank_you_url?.trim() ?? ''
        if (!thankYouUrl) {
          return null
        }
        if (!isSafeThankYouRedirectUrl(thankYouUrl)) {
          return null
        }
        return payload
      } catch {
        // Nova tentativa no intervalo seguinte; esgota e devolve null.
      }
    }

    return null
  })().then((payload) => {
    if (payload === null) {
      thankYouHandoffCache.delete(cacheKey)
    }
    return payload
  })

  thankYouHandoffCache.set(cacheKey, request)
  return request
}

export function resetThankYouResumeConsumeCacheForTests(): void {
  thankYouResumeConsumeCache.clear()
  thankYouHandoffCache.clear()
}

export async function consumeThankYouResume(
  campaignId: number,
  resumeToken: string
): Promise<ThankYouResumePayload> {
  const cacheKey = `${campaignId}:${resumeToken}`
  const cached = thankYouResumeConsumeCache.get(cacheKey)
  if (cached) {
    return cached
  }

  const request = landingApiRequest<ThankYouResumePayload>(
    `/api/campaigns/${campaignId}/payments/pix/thank-you-resume`,
    {
      method: 'POST',
      body: JSON.stringify({ resume_token: resumeToken }),
    }
  ).catch((error: unknown) => {
    thankYouResumeConsumeCache.delete(cacheKey)
    throw error
  })

  thankYouResumeConsumeCache.set(cacheKey, request)
  return request
}
